import { useState } from 'react';

interface ValidationStep {
  id: string;
  name: string;
  description: string;
  completed: boolean;
  loading: boolean;
  result?: string;
}

interface ResourceValidatorProps {
  onComplete: () => void;
}

export default function ResourceValidator({ onComplete }: ResourceValidatorProps) {
  const [steps, setSteps] = useState<ValidationStep[]>([
    {
      id: 'database',
      name: '🗄️ D1 Database',
      description: 'Test CRUD operations',
      completed: false,
      loading: false,
    },
    {
      id: 'cache',
      name: '⚡ KV Cache',
      description: 'Test key-value storage',
      completed: false,
      loading: false,
    },
    {
      id: 'storage',
      name: '📦 R2 Storage',
      description: 'Test file upload/download',
      completed: false,
      loading: false,
    },
    {
      id: 'ai',
      name: '🤖 AI Inference',
      description: 'Test AI capabilities',
      completed: false,
      loading: false,
    },
  ]);

  const [currentStep, setCurrentStep] = useState(0);

  const updateStep = (index: number, updates: Partial<ValidationStep>) => {
    setSteps(prev => prev.map((step, i) =>
      i === index ? { ...step, ...updates } : step
    ));
  };

  const testDatabase = async () => {
    updateStep(0, { loading: true });

    try {
      const response = await fetch('/api/validate/db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (data.success) {
        updateStep(0, {
          loading: false,
          completed: true,
          result: data.message
        });
        setCurrentStep(1);
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      updateStep(0, {
        loading: false,
        result: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    }
  };

  const testCache = async () => {
    updateStep(1, { loading: true });

    try {
      const response = await fetch('/api/validate/cache', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: 'test-key',
          value: 'Hello from FireStarter!'
        }),
      });

      const data = await response.json();

      if (data.success) {
        updateStep(1, {
          loading: false,
          completed: true,
          result: data.message
        });
        setCurrentStep(2);
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      updateStep(1, {
        loading: false,
        result: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    }
  };

  const testStorage = async (file: File) => {
    updateStep(2, { loading: true });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        updateStep(2, {
          loading: false,
          completed: true,
          result: `File uploaded: ${data.data.name}`
        });
        setCurrentStep(3);
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      updateStep(2, {
        loading: false,
        result: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    }
  };

  const testAI = async (prompt: string) => {
    updateStep(3, { loading: true });

    try {
      const response = await fetch('/api/ai/text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });

      const data = await response.json();

      if (data.success) {
        updateStep(3, {
          loading: false,
          completed: true,
          result: data.data.response
        });
        // All tests complete!
        setTimeout(() => onComplete(), 1000);
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      updateStep(3, {
        loading: false,
        result: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    }
  };

  const allComplete = steps.every(s => s.completed);
  const progress = (steps.filter(s => s.completed).length / steps.length) * 100;

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
          🔥 Test Your Resources
        </h1>
        <p style={{ color: '#6b7280' }}>
          Walk through each CloudFlare resource to verify everything works
        </p>
      </div>

      {/* Progress Bar */}
      <div style={{
        width: '100%',
        height: '8px',
        backgroundColor: '#e5e7eb',
        borderRadius: '4px',
        marginBottom: '2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${progress}%`,
          height: '100%',
          backgroundColor: '#10b981',
          transition: 'width 0.3s ease'
        }} />
      </div>

      {/* Steps */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {steps.map((step, index) => (
          <div
            key={step.id}
            style={{
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              padding: '1.5rem',
              backgroundColor: step.completed ? '#f0fdf4' : '#fff',
              borderColor: step.completed ? '#10b981' : '#e5e7eb',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {step.completed ? (
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '14px'
                  }}>✓</div>
                ) : (
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    border: '2px solid #d1d5db',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#9ca3af',
                    fontSize: '12px'
                  }}>{index + 1}</div>
                )}
                <div>
                  <h3 style={{ fontWeight: '600', fontSize: '1.1rem' }}>{step.name}</h3>
                  <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>{step.description}</p>
                </div>
              </div>
            </div>

            {/* Test Controls */}
            {currentStep === index && !step.completed && (
              <div style={{ marginTop: '1rem' }}>
                {step.id === 'database' && (
                  <div>
                    <p style={{ marginBottom: '1rem', color: '#374151' }}>
                      This will create a test table, insert a user, read it back, update it, and delete it.
                    </p>
                    <button
                      onClick={testDatabase}
                      disabled={step.loading}
                      style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: step.loading ? '#9ca3af' : '#ef4444',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: step.loading ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                      }}
                    >
                      {step.loading ? 'Testing Database...' : '🗄️ Test Database CRUD'}
                    </button>
                  </div>
                )}

                {step.id === 'cache' && (
                  <div>
                    <p style={{ marginBottom: '1rem', color: '#374151' }}>
                      This will set a key-value pair in KV storage and retrieve it.
                    </p>
                    <button
                      onClick={testCache}
                      disabled={step.loading}
                      style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: step.loading ? '#9ca3af' : '#ef4444',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: step.loading ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                      }}
                    >
                      {step.loading ? 'Testing Cache...' : '⚡ Test KV Cache'}
                    </button>
                  </div>
                )}

                {step.id === 'storage' && (
                  <div>
                    <p style={{ marginBottom: '1rem', color: '#374151' }}>
                      Upload a test file to R2 storage.
                    </p>
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) testStorage(file);
                      }}
                      disabled={step.loading}
                      style={{
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '6px',
                        width: '100%',
                        cursor: step.loading ? 'not-allowed' : 'pointer',
                      }}
                    />
                  </div>
                )}

                {step.id === 'ai' && (
                  <div>
                    <p style={{ marginBottom: '1rem', color: '#374151' }}>
                      Ask the AI something pithy about CloudFlare Workers!
                    </p>
                    <button
                      onClick={() => testAI('Tell me something pithy about CloudFlare Workers')}
                      disabled={step.loading}
                      style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: step.loading ? '#9ca3af' : '#ef4444',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: step.loading ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                      }}
                    >
                      {step.loading ? 'Asking AI...' : '🤖 Ask AI'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Result */}
            {step.result && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: step.completed ? '#d1fae5' : '#fee2e2',
                borderRadius: '6px',
                fontSize: '0.9rem',
                color: '#374151'
              }}>
                {step.result}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* All Complete */}
      {allComplete && (
        <div style={{
          marginTop: '2rem',
          padding: '2rem',
          backgroundColor: '#d1fae5',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            🎉 All Systems Go!
          </h2>
          <p style={{ color: '#047857' }}>
            Your FireStarter template is fully operational. Start building!
          </p>
        </div>
      )}
    </div>
  );
}
